/**
 * @element qds-user-avatar
 * 
 * A user avatar component that displays an image, falling back to initials after image errors.
 * 
 * @attr {string} name - The user's name (used for initials if no image is provided)
 * @attr {string} image - URL to the user's avatar image
 * @attr {string} status - User status (online, away, busy, offline)
 * 
 * @csspart avatar - The avatar container
 * @csspart image - The avatar image
 * @csspart initials - The initials text
 * @csspart status - The status indicator
 * @csspart avatar-dropdown - The dropdown menu container
 * @csspart menu-item - Individual menu items in the dropdown
 * 
 * @fires click - When the avatar is clicked
 * @fires menu-item-selected - When a dropdown menu item is clicked
 */

import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';

// Load base CSS
loadBaseCSS();

type UserStatus = 'online' | 'away' | 'busy' | 'offline' | '';

export interface UserAvatarMenuItemConfig {
  id: string;
  label: string;
  icon?: string;
  action?: () => void;
}

export class QdsUserAvatar extends HTMLElement {
  // Protected properties so they can be accessed by derived classes
  private _imageFailed = false;
  private _boundImageErrorHandler = (): void => {
    if (!this._image || this._imageFailed) return;
    this._imageFailed = true;
    this.render();
  };
  protected _name: string;
  protected _image: string;
  protected _status: UserStatus;
  protected _boundClickHandler?: EventListener;
  protected _avatarMenuOpen: boolean;
  protected _avatarMenuItems: Map<string, UserAvatarMenuItemConfig>;
  protected _boundAvatarMenuItemClickHandler: (event: Event) => void;
  protected _boundAvatarMenuClickOutsideHandler: (event: MouseEvent) => void;
  protected _boundAvatarMenuKeydownHandler: (event: KeyboardEvent) => void;

  static get observedAttributes(): string[] {
    return ['name', 'image', 'status'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize properties
    this._name = '';
    this._image = '';
    this._status = '';
    this._avatarMenuOpen = false;
    this._avatarMenuItems = new Map();
    this._boundAvatarMenuItemClickHandler = this._handleAvatarMenuItemClick.bind(this);
    this._boundAvatarMenuClickOutsideHandler = this._handleAvatarMenuClickOutside.bind(this);
    this._boundAvatarMenuKeydownHandler = this._handleAvatarMenuKeydown.bind(this);
    
    this.render();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    
    // Bind the click handler for proper cleanup
    this._boundClickHandler = this._boundClickHandler || ((event: Event) => this._handleClick(event as MouseEvent));
    
    this._attachAvatarInteractionHandlers();
  }

  disconnectedCallback(): void {
    this._detachAvatarInteractionHandlers();
    this._removeAvatarMenuListeners();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'name':
        this._name = newValue || '';
        break;
      case 'image':
        this._imageFailed = false;
        this._image = newValue || '';
        break;
      case 'status':
        this._status = this._validateStatus(newValue);
        break;
    }

    this.render();
  }

  // Validate and normalize status value
  private _validateStatus(value: string): UserStatus {
    if (!value) return '';
    
    const normalizedValue = value.toLowerCase();
    const validStatuses: UserStatus[] = ['online', 'away', 'busy', 'offline'];
    
    return validStatuses.includes(normalizedValue as UserStatus) 
      ? normalizedValue as UserStatus 
      : '';
  }

  get name(): string {
    return this._name;
  }

  set name(value: string) {
    this.setAttribute('name', value);
  }

  get image(): string {
    return this._image;
  }

  set image(value: string) {
    this.setAttribute('image', value);
  }

  get status(): UserStatus {
    return this._status;
  }

  set status(value: UserStatus) {
    this.setAttribute('status', value);
  }

  protected _getInitials(): string {
    if (!this._name) return '';
    
    // Get initials from name (up to 2 characters)
    return this._name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  protected _getStatusColor(): string {
    switch (this._status.toLowerCase()) {
      case 'online':
        return 'var(--avatar-status-online-color, var(--theme-success))';
      case 'away':
        return 'var(--avatar-status-away-color, var(--theme-warning))';
      case 'busy':
        return 'var(--avatar-status-busy-color, var(--theme-error))';
      case 'offline':
        return 'var(--avatar-status-offline-color, var(--theme-text-tertiary))';
      default:
        return 'transparent';
    }
  }

  protected _handleClick(event: MouseEvent): void {
    if (this._avatarMenuItems.size > 0) {
      event.stopPropagation();
      this._toggleAvatarMenu();
    }

    // Dispatch a custom event when clicked
    this.dispatchEvent(new CustomEvent('click', {
      bubbles: true,
      composed: true
    }));
  }

  public addMenuItem(id: string, config: UserAvatarMenuItemConfig): void {
    this._avatarMenuItems.set(id, { ...config, id });
    this.render();
  }

  public removeMenuItem(id: string): void {
    this._avatarMenuItems.delete(id);

    if (!this._avatarMenuItems.size) {
      this._avatarMenuOpen = false;
    }

    this.render();
  }

  public getMenuItems(): UserAvatarMenuItemConfig[] {
    return Array.from(this._avatarMenuItems.values());
  }

  public clearMenuItems(): void {
    this._avatarMenuItems.clear();
    this._avatarMenuOpen = false;
    this.render();
  }

  protected _renderAvatarMenu(): string {
    if (!this._avatarMenuItems.size) {
      return '';
    }

    return `
      <div class="avatar-dropdown ${this._avatarMenuOpen ? 'open' : ''}" part="avatar-dropdown">
        <div class="avatar-dropdown-items">
          ${Array.from(this._avatarMenuItems.values()).map((item) => {
            if (item.label === '-') {
              return '<div class="avatar-dropdown-separator"></div>';
            }

            return `
              <button class="avatar-dropdown-item" data-item-id="${item.id}" part="menu-item" type="button">
                ${item.icon ? `<div class="avatar-dropdown-item-icon">${item.icon}</div>` : ''}
                <div class="avatar-dropdown-item-label">${this._escapeMenuHtml(item.label)}</div>
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  protected _handleAvatarMenuItemClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const button = target?.closest('.avatar-dropdown-item') as HTMLElement | null;
    const itemId = button?.getAttribute('data-item-id');

    if (!itemId) {
      return;
    }

    const item = this._avatarMenuItems.get(itemId);
    if (!item) {
      return;
    }

    this._closeAvatarMenu();

    if (typeof item.action === 'function') {
      try {
        item.action();
      } catch (error) {
        console.error('Error executing avatar menu item action:', error);
      }
    }

    this.dispatchEvent(new CustomEvent('menu-item-selected', {
      bubbles: true,
      composed: true,
      detail: { itemId, itemLabel: item.label }
    }));
  }

  protected _toggleAvatarMenu(): void {
    if (!this._avatarMenuItems.size) {
      return;
    }

    this._avatarMenuOpen = !this._avatarMenuOpen;
    this.render();
  }

  protected _closeAvatarMenu(): void {
    if (!this._avatarMenuOpen) {
      return;
    }

    this._avatarMenuOpen = false;
    this.render();
  }

  protected _handleAvatarMenuClickOutside(event: MouseEvent): void {
    if (!this._avatarMenuOpen || !this.shadowRoot) {
      return;
    }

    const dropdown = this.shadowRoot.querySelector('.avatar-dropdown-wrapper');
    const target = event.target as Node;

    if (dropdown && !dropdown.contains(target)) {
      this._closeAvatarMenu();
    }
  }

  protected _handleAvatarMenuKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !this._avatarMenuOpen) {
      return;
    }

    this._closeAvatarMenu();

    const avatar = this.shadowRoot?.querySelector('.avatar');
    if (avatar instanceof HTMLElement) {
      avatar.focus();
    }
  }

  protected _attachAvatarInteractionHandlers(): void {
    const image = this.shadowRoot?.querySelector('img.avatar-image');
    if (image) {
      image.removeEventListener('error', this._boundImageErrorHandler);
      image.addEventListener('error', this._boundImageErrorHandler);
      if ((image as HTMLImageElement).complete && !(image as HTMLImageElement).naturalWidth) {
        this._boundImageErrorHandler();
        return;
      }
    }
    const avatar = this.shadowRoot?.querySelector('.avatar');
    if (avatar && this._boundClickHandler) {
      avatar.addEventListener('click', this._boundClickHandler);
    }

    const dropdown = this.shadowRoot?.querySelector('.avatar-dropdown');
    if (dropdown) {
      dropdown.removeEventListener('click', this._boundAvatarMenuItemClickHandler);
      dropdown.addEventListener('click', this._boundAvatarMenuItemClickHandler);
    }

    if (this._avatarMenuOpen && this._avatarMenuItems.size > 0) {
      this._addAvatarMenuListeners();
    } else {
      this._removeAvatarMenuListeners();
    }
  }

  protected _detachAvatarInteractionHandlers(): void {
    this.shadowRoot?.querySelector('img.avatar-image')?.removeEventListener('error', this._boundImageErrorHandler);
    const avatar = this.shadowRoot?.querySelector('.avatar');
    if (avatar && this._boundClickHandler) {
      avatar.removeEventListener('click', this._boundClickHandler);
    }

    const dropdown = this.shadowRoot?.querySelector('.avatar-dropdown');
    if (dropdown) {
      dropdown.removeEventListener('click', this._boundAvatarMenuItemClickHandler);
    }
  }

  protected _addAvatarMenuListeners(): void {
    document.removeEventListener('click', this._boundAvatarMenuClickOutsideHandler);
    document.removeEventListener('keydown', this._boundAvatarMenuKeydownHandler);

    setTimeout(() => {
      if (this._avatarMenuOpen) {
        document.addEventListener('click', this._boundAvatarMenuClickOutsideHandler);
      }
    }, 0);

    document.addEventListener('keydown', this._boundAvatarMenuKeydownHandler);
  }

  protected _removeAvatarMenuListeners(): void {
    document.removeEventListener('click', this._boundAvatarMenuClickOutsideHandler);
    document.removeEventListener('keydown', this._boundAvatarMenuKeydownHandler);
  }

  protected _escapeMenuHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  protected render(): void {
    if (!this.shadowRoot) return;
    this._detachAvatarInteractionHandlers();
    
    const initials = this._getInitials();
    const hasImage = Boolean(this._image) && !this._imageFailed;
    const showStatus = Boolean(this._status);
    const hasMenuItems = this._avatarMenuItems.size > 0;
        
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --_avatar-size: var(--avatar-size, 40px);
          --_avatar-background: var(--avatar-bg-color, var(--theme-accent-primary));
          --_avatar-text: var(--avatar-text-color, var(--theme-text-on-accent-primary));
          --_avatar-hover-opacity: var(--avatar-hover-opacity, 0.9);
          --_avatar-status-size: var(--avatar-status-size, 12px);
          --_avatar-status-border-width: var(--avatar-status-border-width, 2px);
          --_avatar-status-border-color: var(--status-border-color, var(--theme-background-primary));
          display: inline-block;
          color: var(--_avatar-text);
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
        }

        .avatar-container {
          position: relative;
          width: var(--_avatar-size);
          height: var(--_avatar-size);
          display: inline-block;
        }

        .avatar-dropdown-wrapper {
          position: relative;
          display: inline-block;
        }

        .avatar {
          width: var(--_avatar-size);
          height: var(--_avatar-size);
          border-radius: 50%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--_avatar-background);
          color: var(--_avatar-text);
          font-weight: var(--theme-font-weight-medium, 500);
          cursor: pointer;
          transition:
            opacity var(--transition-speed-fast, 0.15s) ease,
            background-color var(--transition-speed-fast, 0.15s) ease,
            color var(--transition-speed-fast, 0.15s) ease;
        }

        .avatar[aria-haspopup='menu'] {
          position: relative;
        }

        .avatar:hover {
          opacity: var(--_avatar-hover-opacity);
        }

        .avatar:focus-visible {
          outline: 2px solid var(--theme-accent-primary);
          outline-offset: 2px;
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .initials {
          font-size: var(--avatar-initials-size, var(--theme-font-size-md, 16px));
          line-height: 1;
        }

        .status {
          position: absolute;
          bottom: -2px;
          right: -2px;
          width: var(--_avatar-status-size);
          height: var(--_avatar-status-size);
          border-radius: 50%;
          border: var(--_avatar-status-border-width) solid var(--_avatar-status-border-color);
          background-color: ${this._getStatusColor()};
          z-index: 1;
        }

        .avatar-dropdown {
          --_avatar-dropdown-surface: var(--avatar-dropdown-background, var(--theme-background-elevated, var(--theme-background-primary)));
          --_avatar-dropdown-border: var(--avatar-dropdown-border-color, var(--theme-border-primary));
          --_avatar-dropdown-shadow: var(--avatar-dropdown-shadow, var(--theme-shadow-sm));
          --_avatar-dropdown-text: var(--avatar-dropdown-text-color, var(--theme-text-primary));
          --_avatar-dropdown-subtle-text: var(--avatar-dropdown-subtle-text-color, var(--theme-text-secondary));
          --_avatar-dropdown-hover: var(--avatar-dropdown-hover-background, var(--theme-background-hover));
          --_avatar-dropdown-active: var(--avatar-dropdown-active-background, var(--theme-background-tertiary));
          position: absolute;
          top: 100%;
          right: 0;
          min-width: 200px;
          margin-top: var(--theme-space-2, 8px);
          border: 1px solid var(--_avatar-dropdown-border);
          border-radius: var(--theme-radius-md, 8px);
          background-color: var(--_avatar-dropdown-surface);
          box-shadow: var(--_avatar-dropdown-shadow);
          color: var(--_avatar-dropdown-text);
          opacity: 0;
          visibility: hidden;
          transform: translateY(-8px);
          transition:
            opacity var(--transition-speed-normal, 0.25s) ease,
            visibility var(--transition-speed-normal, 0.25s) ease,
            transform var(--transition-speed-normal, 0.25s) ease;
          z-index: 1000;
        }

        .avatar-dropdown.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .avatar-dropdown-items {
          padding: 8px 0;
        }

        .avatar-dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          border: none;
          background: none;
          color: var(--_avatar-dropdown-text);
          padding: 10px 16px;
          text-align: left;
          cursor: pointer;
          font-size: var(--theme-font-size-sm, 14px);
          transition: background-color var(--transition-speed-fast, 0.15s) ease;
        }

        .avatar-dropdown-item:hover {
          background-color: var(--_avatar-dropdown-hover);
        }

        .avatar-dropdown-item:active {
          background-color: var(--_avatar-dropdown-active);
        }

        .avatar-dropdown-item:focus-visible {
          outline: 2px solid var(--theme-accent-primary);
          outline-offset: -2px;
        }

        .avatar-dropdown-item-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--_avatar-dropdown-subtle-text);
          flex-shrink: 0;
        }

        .avatar-dropdown-item-icon svg {
          width: 100%;
          height: 100%;
        }

        .avatar-dropdown-item-icon .pi {
          font-size: var(--icon-size, 20px);
          line-height: 1;
          color: currentColor;
        }

        .avatar-dropdown-item-label {
          flex: 1;
        }

        .avatar-dropdown-separator {
          height: 1px;
          margin: 8px 0;
          background-color: var(--_avatar-dropdown-border);
        }
      </style>

      ${hasMenuItems ? '<div class="avatar-dropdown-wrapper">' : ''}
        <div class="avatar-container">
          <div
            class="avatar"
            part="avatar"
            tabindex="0"
            role="button"
            aria-label="${this._escapeMenuHtml(this._name || 'User avatar')}"
            ${hasMenuItems ? `aria-haspopup="menu" aria-expanded="${this._avatarMenuOpen ? 'true' : 'false'}"` : ''}
          >
            ${hasImage
              ? `<img src="${this._escapeMenuHtml(this._image)}" alt="${this._escapeMenuHtml(this._name || 'User avatar')}" part="image" class="avatar-image">`
              : `<span class="initials" part="initials">${this._escapeMenuHtml(initials)}</span>`
            }
          </div>
          ${showStatus ? `<span class="status" part="status"></span>` : ''}
        </div>
        ${hasMenuItems ? this._renderAvatarMenu() : ''}
      ${hasMenuItems ? '</div>' : ''}
    `;
    void adoptPrimeIconsStyles(this.shadowRoot);

    if (this.isConnected) {
      this._attachAvatarInteractionHandlers();
    }
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-user-avatar')) {
  customElements.define('qds-user-avatar', QdsUserAvatar);
}
